import { useState, useEffect, ChangeEvent, SyntheticEvent, useMemo } from 'react';
import { useTheme, Box, Grid, Card, Button, Divider, MenuItem, TextField, InputLabel, Typography, CardContent, FormControl, Select, Checkbox, FormGroup, FormControlLabel, useMediaQuery } from '@mui/material';
import axios from 'axios';
import Title from '../../components/Title';
import { Office } from '../../models/office';
import { Collect } from '../../models/collect';
import { reportLend } from '../../models/reportlend';
import moment from "moment-timezone";
import { nowOperationalDateString } from '../../utils/operationalDateTime';
import { formatCurrency } from '../../utils/format/currency';
import ModalOfficeExpense from './modals/ModalOfficeExpense';
import ModalLendRefinance from './modals/ModalLendRefinance';
import SimpleCustomTable from '../../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow, RowId } from '../../components/table/interface';
import CollectFilter from '../../components/filter/collectbyDate';
import HeaderResponsive from '../../components/layouts/HeaderResponsive';
import { stickyListRegionWidth } from '../../components/layouts/StickyListToolbar';
import { Search as SearchIcon, Info as InfoIcon } from '@mui/icons-material';
import EmptyStateMessage from '../../components/layouts/EmptyStateMessage';
import { useDrawer } from '../../components/context/DrawerContext';

/** Fila sin movimientos: abonos, préstamos, refinanciados, gastos oficina (+/−) y gastos caja cobro en 0. */
const RESUMEN_ROW_NO_MOVEMENT_BG = '#ef535082';
/** Filas de resumen (Promedio y Total). */
const RESUMEN_ROW_SUMMARY_BG = '#ffd3b8';
/** Columnas Efectivo y Base en Resumen Día. */
const RESUMEN_CASH_BASE_COLUMN_BG = '#50afef82';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const ResumenDia: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = "Resumen Día";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const current_date = nowOperationalDateString();
  const { drawerOpen } = useDrawer(); // ya lo usas en tu componente

  const [office_id, setOffice_id] = useState('');
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [start_date, setStart_date] = useState(current_date);
  const [end_date, setEnd_date] = useState(current_date);
  const [datas, setDatas] = useState<any[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [openModalRefi, setOpenModalRefi] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [modalData, setModalData] = useState<{ boxId: number; date: string; dateISO?: string; collect: string; collectId?: number | string; typeOperation: number; out?: boolean } | null>(null);
  const [modalLend, setModalLend] = useState<{
    boxId: number;
    date_start: string;
    date_end: string;
    collect: string;
    collect_id: string;
  } | null>(null);

  useEffect(() => {
    axios.get('offices').then(({ data }) => setOffices(data.data));
  }, []);

  useEffect(() => {
    if (office_id) {
      axios.get(`collectsoffice/${office_id}`).then(({ data }) => {
        setCollectsOffice(data);
        setSelectedCollects([]);
        setSelectAll(false);
      });
    }
  }, [office_id]);

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedCollects(prev =>
      prev.includes(value) ? prev.filter(id => id !== value) : [...prev, value]
    );
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = collectsOffice.map(collect => collect.id.toString());
      setSelectedCollects(allIds);
      setSelectAll(true);
    } else {
      setSelectedCollects([]);
      setSelectAll(false);
    }
  };

  const handleOpenModal = (row: any) => {
    setSelectedRow(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalData(null);
    setSelectedRow(null);
  };
  const handleCloseModalLend = () => {
    setOpenModalRefi(false);
    setModalLend(null);
    setSelectedRow(null);
  };
  const handleOpenModalWithType = (row: any, typeOperation: number) => {
    // API ModalExpense: type 1 + out true incluye traslados a caja cobro y gastos oficina (origen=caja, destino null).
    // Enviar type_operation 1 para "Gastos Oficina Suma", no 2 (2 + out usa solo boxDestination).
    setModalData({
      boxId: row.boxId,
      date: row.date,
      dateISO: row.dateISO,
      collect: row.collect,
      collectId: row.collect_id,
      typeOperation: typeOperation === 1 ? 1 : typeOperation === 2 ? 2 : 3,
      out: typeOperation === 1,
    });
    setOpenModal(true);
  };
  const handleOpenModalLendType = (row: any) => {
    setModalLend({
      boxId: row.boxId,
      date_start: start_date,
      date_end: end_date,
      collect: row.collect,
      collect_id: String(row.collect_id),
    });
    setOpenModalRefi(true);
  };
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!selectedCollects || selectedCollects.length === 0) {
      showAlert('Debe seleccionar almenos 1 cobro', 'warning');
      return;
    }
    const formData = { end_date, start_date, collect_id: selectedCollects };
    showLoading();
    try {
      const response = await axios.post('/inform/summaryday', formData);
      setDatas(response.data.data);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!response.data.data || response.data.data.length === 0) {
        showAlert('No existen registros para los datos seleccionados', 'info');
        return;
      }

    } catch (error: any) {
      console.error('Error:', error);
      if (error?.response?.status === 403) {
        showAlert('No tiene permisos para consultar el resumen del día.', 'error');
        return;
      }
      showAlert('Error consultando el resumen del día.', 'error');
    } finally {
      hideLoading();
    }
  };

  const numericColumns = [
    'total_pays', 'total_lends', 'total_utility', 'value_refinance',
    'total_insurance_charge',
    'total_refinance', 'total_box_adjustments', 'total_off_exp_plus', 'total_off_exp_minus',
    'total_expenses', 'cash', 'base', 'box', 'cash_short_over',
  ];

  const isSummaryRow = (row: SimpleRow) => row.id === 'total-row' || row.id === 'average-row';

  const rowNumeric = (row: SimpleRow, key: string) => {
    const v = row[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0;
  };

  const isDataRowWithoutMovements = (row: SimpleRow) =>
    !isSummaryRow(row) &&
    rowNumeric(row, 'total_pays') === 0 &&
    rowNumeric(row, 'total_lends') === 0 &&
    rowNumeric(row, 'total_insurance_charge') === 0 &&
    rowNumeric(row, 'value_refinance') === 0 &&
    rowNumeric(row, 'total_refinance') === 0 &&
    rowNumeric(row, 'total_off_exp_plus') === 0 &&
    rowNumeric(row, 'total_off_exp_minus') === 0 &&
    rowNumeric(row, 'total_box_adjustments') === 0 &&
    rowNumeric(row, 'total_expenses') === 0;

  const cashBaseColumnHeaderCellSx = { bgcolor: RESUMEN_CASH_BASE_COLUMN_BG } as const;
  const cashBaseColumnBodyCellSx = {
    bgcolor: RESUMEN_CASH_BASE_COLUMN_BG,
    verticalAlign: 'middle' as const,
  };
  const getCashBaseColumnCellSx = () => cashBaseColumnBodyCellSx;

  const renderCurrencyCell = (field: string) => ({ row }: { row: SimpleRow }) => {
    const value = row[field];

    if (isSummaryRow(row)) {
      return <Typography variant="body2">{value ?? '-'}</Typography>;
    }

    const numericValue = typeof value === 'number' ? value : Number(value) || 0;
    return <Typography variant="body2">{(numericValue)}</Typography>;
  };

  const renderCurrencyCellWithButton = (
    field: string,
    options: {
      typeOperation?: number;
      onClick?: (row: SimpleRow) => void;
    } = {}
  ) => ({ row }: { row: SimpleRow }) => {
    const value = row[field];

    if (isSummaryRow(row)) {
      return <Typography variant="body2">{value ?? '-'}</Typography>;
    }

    const { typeOperation, onClick } = options;

    const numericValue = typeof value === 'number' ? value : Number(value) || 0;
    const formattedValue = (numericValue);

    const handleClick = () => {
      if (onClick) {
        onClick(row);
        return;
      }

      if (typeof typeOperation === 'number') {
        handleOpenModalWithType(row, typeOperation);
        return;
      }

      handleOpenModal(row);
    };

    const valueNode = <Typography variant="body2">{formattedValue}</Typography>;
    const buttonNode = (
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={handleClick}
        sx={{ minWidth: 0, padding: '2px', minHeight: 'unset', lineHeight: 1, flexShrink: 0 }}
      >
        <SearchIcon fontSize="small" />
      </Button>
    );

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        width="100%"
        gap={1}
      >
        {valueNode}
        {buttonNode}
      </Box>
    );
  };

  const renderRefinanceCell = (field: string) =>
    renderCurrencyCellWithButton(field, { onClick: handleOpenModalLendType });

  const tableColumns = useMemo<SimpleColumn[]>(() => [
    {
      field: 'collect',
      headerName: 'Cobro',
      minWidth: 100,
      wrapText: true,
    },
    {
      field: 'date',
      headerName: 'Fecha',
      minWidth: 80,
    },
    {
      field: 'total_pays',
      headerName: 'Abonos',
      align: 'right',
      minWidth: 40,
      maxWidth: 70,
      renderCell: renderCurrencyCell('total_pays'),
    },
    {
      field: 'total_lends',
      headerName: 'Préstamos',
      align: 'right',
      minWidth: 40,
      maxWidth: 80,
      renderCell: renderCurrencyCell('total_lends'),
    },
    {
      field: 'total_utility',
      headerName: 'Utilidad',
      align: 'right',
      minWidth: 40,
      maxWidth: 75,
      renderCell: renderCurrencyCell('total_utility'),
    },
    {
      field: 'total_off_exp_plus',
      headerName: 'Gastos Oficina +',
      align: 'right',
      minWidth: 50,
      maxWidth: 85,
      renderCell: renderCurrencyCellWithButton('total_off_exp_plus', { typeOperation: 1 }),
    },
    {
      field: 'total_off_exp_minus',
      headerName: 'Gastos Oficina -',
      align: 'right',
      minWidth: 50,
      maxWidth: 85,
      renderCell: renderCurrencyCellWithButton('total_off_exp_minus', { typeOperation: 2 }),
    },
    {
      field: 'total_expenses',
      headerName: 'Gastos Caja Cobro',
      align: 'right',
      minWidth: 50,
      maxWidth: 85,
      renderCell: renderCurrencyCellWithButton('total_expenses', { typeOperation: 3 }),
    },
    {
      field: 'cash',
      headerName: 'Efectivo',
      align: 'right',
      minWidth: 40,
      maxWidth: 80,
      headerCellSx: cashBaseColumnHeaderCellSx,
      getCellSx: getCashBaseColumnCellSx,
      renderHeader: () => (
        <Typography variant="body2" color="text.primary" fontWeight={600} component="span">
          Efectivo
        </Typography>
      ),
      renderCell: renderCurrencyCell('cash'),
    },
    {
      field: 'base',
      headerName: 'Base',
      align: 'right',
      minWidth: 50,
      maxWidth: 60,
      headerCellSx: cashBaseColumnHeaderCellSx,
      getCellSx: getCashBaseColumnCellSx,
      renderHeader: () => (
        <Typography variant="body2" color="text.primary" fontWeight={600} component="span">
          Base
        </Typography>
      ),
      renderCell: renderCurrencyCell('base'),
    },
    {
      field: 'box',
      headerName: 'Total Caja',
      align: 'right',
      minWidth: 60,
      maxWidth: 70,
      renderCell: renderCurrencyCell('box'),
    },
    {
      field: 'total_insurance_charge',
      headerName: 'Gastos Gestión',
      align: 'right',
      minWidth: 60,
      maxWidth: 70,
      renderCell: renderCurrencyCell('total_insurance_charge'),
    },
    {
      field: 'cash_short_over',
      headerName: 'Descuadre',
      align: 'right',
      minWidth: 60,
      maxWidth: 80,
      renderCell: renderCurrencyCell('cash_short_over'),
    },
    {
      field: 'pay',
      headerName: 'Pagos',
      minWidth: 40,
      maxWidth: 80,
      align: 'right',
    },
    {
      field: 'pay_percentage',
      headerName: '% Pago',
      align: 'right',
      minWidth: 50,
      maxWidth: 80,
      renderCell: ({ row }) => {
        const value = row.pay_percentage;
        if (isSummaryRow(row)) {
          return <Typography variant="body2">{value ?? '-'}</Typography>;
        }

        const numericValue = typeof value === 'number' ? value : Number(value) || 0;
        return <Typography variant="body2">{`${numericValue.toFixed(2)}%`}</Typography>;
      },
    },
    {
      field: 'total_refinance',
      headerName: 'Util Refinan',
      align: 'right',
      minWidth: 60,
      maxWidth: 90,
      renderCell: renderCurrencyCell('total_refinance'),
    },
    {
      field: 'value_refinance',
      headerName: 'Vlr Refinan',
      align: 'right',
      minWidth: 40,
      maxWidth: 90,
      renderCell: renderRefinanceCell('value_refinance'),
    },

  ], [handleOpenModalLendType, handleOpenModalWithType]);

  const renderTablePorCobro = (groupData: reportLend[]) => {
    const sortedRows = [...groupData].sort((a, b) => {
      const da = typeof a.date === 'string' ? a.date : moment(a.date).format('YYYY-MM-DD');
      const db = typeof b.date === 'string' ? b.date : moment(b.date).format('YYYY-MM-DD');
      const c = String(da).localeCompare(String(db));
      if (c !== 0) return c;
      return Number(a.id) - Number(b.id);
    });

    const dataRows: SimpleRow[] = sortedRows.map((r) => {
      const rawBox = r.box as unknown;
      const boxIdentifier =
        (rawBox && typeof rawBox === 'object' && 'id' in rawBox
          ? Number((rawBox as { id?: number }).id)
          : undefined) ?? (r as unknown as { box_id?: number }).box_id ?? (r as unknown as { boxId?: number }).boxId ?? 0;

      return {
        id: r.id as RowId,
        collect: r.collect.name,
        collect_id: r.collect.id,
        boxId: boxIdentifier,
        date: r.date ? moment(r.date).format('YYYY/MM/DD') : '',
        dateISO: r.date ? moment(r.date).format('YYYY-MM-DD') : '',
        hour: r.date ? moment(r.date).format('HH:mm:ss') : '',
        pay: `${r.count_pays} de ${r.count_client}`,
        pay_percentage: r.count_client > 0 ? (r.count_pays / r.count_client) * 100 : 0,
        total_pays: r.total_pays,
        total_lends: r.total_lends,
        total_utility: r.total_utility,
        total_insurance_charge: r.total_insurance_charge,
        value_refinance: r.value_refinance,
        total_refinance: r.total_refinance,
        total_off_exp_plus: r.total_off_exp_plus,
        total_off_exp_minus: r.total_off_exp_minus,
        total_box_adjustments: r.total_box_adjustments ?? 0,
        total_expenses: r.total_expenses,
        cash: r.cash,
        base: r.base,
        box: r.value_box,
        cash_short_over: r.cash_short_over,
      } as SimpleRow;
    });

    const rowsWithOperation = dataRows.filter((row) => !isDataRowWithoutMovements(row));
    const operationDayCount = rowsWithOperation.length;

    const totalValues: Record<string, number> = {};
    const averageValues: Record<string, number> = {};

    numericColumns.forEach((col) => {
      const sum = dataRows.reduce((acc, row) => acc + rowNumeric(row, col), 0);
      totalValues[col] = sum;
      if (operationDayCount === 0) {
        averageValues[col] = 0;
      } else {
        const opSum = rowsWithOperation.reduce((acc, row) => acc + rowNumeric(row, col), 0);
        averageValues[col] = opSum / operationDayCount;
      }
    });

    const tableRows: SimpleRow[] = [
      ...dataRows,
      {
        id: 'total-row',
        collect: 'Total',
        ...Object.fromEntries(numericColumns.map((col) => [col, formatCurrency(totalValues[col])])),
      },
      {
        id: 'average-row',
        collect: 'Promedio',
        ...Object.fromEntries(numericColumns.map((col) => [col, formatCurrency(averageValues[col])])),
      }
    ];


    return (
      <>
        <SimpleCustomTable
          rows={tableRows}
          columns={tableColumns}
          disableInternalScroll="vertical"
          showBorders
          showCount
          exportCsv
          exportFileName={`resumen-dia-${sortedRows[0]?.collect?.name ?? 'grupo'}.csv`}
          wrapCellContent
          getRowCellSx={({ row }) =>
            isSummaryRow(row)
              ? {
                backgroundColor: `${RESUMEN_ROW_SUMMARY_BG} !important`,
                verticalAlign: 'middle' as const,
                borderLeft: '1px solid #000',
                borderRight: '1px solid #000',
                borderBottom: '1px solid #000',
                borderTop: '1px solid #000',
                '&:last-child': {
                  borderRight: '1px solid #000',
                },
              }
              : isDataRowWithoutMovements(row)
                ? {
                  backgroundColor: `${RESUMEN_ROW_NO_MOVEMENT_BG} !important`,
                  verticalAlign: 'middle' as const,
                }
                : undefined
          }
        />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Se encontraron {sortedRows.length} registros para {sortedRows[0]?.collect?.name}
        </Typography>
      </>
    );
  };
  return (
    <HeaderResponsive sx={{ py: 0, px: 0.5, pb: { xs: 10, md: 8 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} flexShrink={0}>
        <Title>Resumen Día</Title>
        <div style={{ marginLeft: "auto" }}>
          <Button variant="contained" onClick={submit} startIcon={!isMobile && <SearchIcon />}>
            {isMobile ? <SearchIcon /> : 'Buscar'}
          </Button>
        </div>
      </Box>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            width: (t) => stickyListRegionWidth(t, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
            flexShrink: 0,
            scrollbarColor: '#0768c9 #d9e1ea',
            '&::-webkit-scrollbar': {
              width: 12,
              height: 12,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#d9e1ea',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#0768c9',
              borderRadius: 999,
              border: '2px solid #d9e1ea',
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: '#d9e1ea',
            },
          }}
        >
          <CollectFilter
            start_date={start_date}
            end_date={end_date}
            office_id={office_id}
            setStart_date={setStart_date}
            setEnd_date={setEnd_date}
            setOffice_id={setOffice_id}
            offices={offices}
            collectsOffice={collectsOffice}
            selectedCollects={selectedCollects}
            setSelectedCollects={setSelectedCollects}
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            handleCheckboxChange={handleCheckboxChange}
            handleSelectAll={handleSelectAll}
          />
          <Divider sx={{ my: 1 }} />
        </CardContent>
        {datas.length > 0 ? (
          datas.map((grupo: any) => (
            <Box key={grupo.collect_id} mb={4} p={2} borderRadius={1} bgcolor="background.paper" boxShadow={3} sx={{ fontSize: '0.9rem' }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                <strong>Resumen Día - </strong> {grupo.collect_name}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Fechas:</strong> {start_date} al {end_date}
                  </Typography>
                </Grid>
              </Grid>
              {renderTablePorCobro(grupo.data)}
            </Box>
          ))
        ) : (
          <EmptyStateMessage message="No existen registros" align="left" />
        )}
        <ModalOfficeExpense
          open={openModal}
          onClose={handleCloseModal}
          clientData={modalData}
          showAlert={showAlert}
          showLoading={showLoading}
          hideLoading={hideLoading} />
        <ModalLendRefinance
          open={openModalRefi}
          onClose={handleCloseModalLend}
          clientLend={modalLend}
          showAlert={showAlert}
          showLoading={showLoading}
          hideLoading={hideLoading} />
      </Card>
    </HeaderResponsive>
  );
};

export default ResumenDia;
